// The DOM that `text-angular` builds, reproduced (vendor/textAngular/src/main.js:80-320).
//
// ⚠️ THE STYLESHEET IS WRITTEN AGAINST A DOM THE DIRECTIVE GENERATES, NOT AGAINST THE MARKUP IN
// THE TEMPLATE. `<div class="candidate-message-body" text-angular …>` becomes, at link time:
//
//   <div class="candidate-message-body ta-root">          ← the element keeps its own classes
//     <div class="ta-scroll-window ta-text ta-editor form-control candidate-message-body-editor …">
//       <div class="popover fade bottom">…</div>
//       <div id="candidate-message-body" class="ta-bind ta-text placeholder-text" contenteditable>
//     <textarea class="ta-html ta-editor form-control …" style="display:none">
//     <input type="hidden" tabindex="-1" style="display:none">
//
// `.ta-scroll-window.form-control` carries `min-height: 300px; padding: 0` and `.ta-scroll-window
// > .ta-bind` carries `padding: 6px 12px` — so an editor rendered as one `div.form-control`
// gets the padding once instead of on the inner box, and every editor on the screen is the
// wrong height. The nesting is the layout.
import { useId, useLayoutEffect, useRef, type ReactElement } from 'react'
import { taDefaultValue, taEditorFromModel, taModelFromEditor } from './taDefaultWrap.ts'

export interface RichTextEditorProps {
  /**
   * The element id, which is NOT cosmetic: textAngular's placeholder is a generated CSS rule
   * `#<id>.placeholder-text:before { content: "<placeholder>" }` (taBind.js:866), so an editor
   * without a stable id has no placeholder at all. Upstream throws rather than allow it.
   */
  id: string
  /** The classes the template puts on the element, beside `ta-root`. */
  className?: string
  /** `ta-text-editor-class` — appended to the scroll window, after `form-control`. */
  editorClassName?: string
  placeholder?: string
  /** `ng-model`. Server-authored HTML, rendered as HTML — this is a WYSIWYG box. */
  html?: string
  /**
   * `ta-default-wrap` — the block element top-level content is wrapped in, in the MODEL as well as
   * on screen (see `taDefaultWrap.ts`). Set it exactly where the template does: upstream's body
   * and signature editors both say `ta-default-wrap="div"`. Absent, the editor reports its
   * `innerHTML` untouched — which is NOT textAngular's own default (`p`), so leaving it off a
   * ported editor is a decision, not a neutral choice.
   */
  defaultWrap?: string
  /** `ta-disabled` — the signature editor is disabled until the pencil is clicked. */
  disabled?: boolean
  onChange?: (html: string) => void
  onBlur?: () => void
  onClick?: () => void
}

export function RichTextEditor({
  id,
  className = '',
  editorClassName = '',
  placeholder,
  html = '',
  defaultWrap,
  disabled = false,
  onChange,
  onBlur,
  onClick,
}: RichTextEditorProps): ReactElement {
  // A scoped name for the generated placeholder rule, so two editors with the same placeholder
  // on one screen still get one rule each — as `addCSSRule` does per id.
  const styleKey = useId()
  // ⚠️ A DISABLED EDITOR SHOWS NO PLACEHOLDER. taBind guards the whole placeholder mechanism on
  // `_isContentEditable && attrs.placeholder` (taBind.js:986), and a `ta-disabled` editor is not
  // contenteditable — so the signature box, which is disabled until the pencil is clicked, is
  // BLANK rather than reading "Add your signature". The placeholder is declared in the template
  // all the same, and only appears once editing starts.
  const showsPlaceholder = html === '' && !disabled

  /**
   * ⚠️ THE CONTENT IS WRITTEN BY HAND, AND `dangerouslySetInnerHTML` HERE REVERSED EVERY WORD
   * THE USER TYPED. With the HTML set as a React prop, each keystroke re-rendered the div from
   * `html` — React replaces the text node, the browser collapses the caret to offset 0, and
   * the next character lands in FRONT of the last one. Measured in the real composer:
   * "Probe body" arrived at the server as "ydob eborP". It was invisible until Send was wired,
   * because nothing had ever read the body back.
   *
   * So the element's children are React's business no longer. The effect writes `html` only
   * when it DIFFERS from what is in the DOM, which is exactly when the model changed from
   * outside — a template being applied, a draft loading, the signature arriving — and never on
   * the keystroke the element itself just reported. That is textAngular's own arrangement
   * (`taBind` writes the element on a model change and reads it on input), which the note this
   * replaces claimed to mirror and did not.
   *
   * `useLayoutEffect` rather than `useEffect`: it runs before paint, so an editor that mounts
   * with content never shows one empty frame — which a reference capture would photograph.
   *
   * ⚠️ WITH `defaultWrap` THE MODEL IS NOT THE DOM, so "differs from the DOM" no longer means
   * "changed from outside": the editor reports `<div>x</div>` for a DOM holding a bare `x`
   * (select-all and type can leave one), and writing that back would move the caret exactly as
   * above. So the value this element last reported is remembered and never written back — taBind
   * does the same with `_skipRender` (taBind.js:304-319) — and only a value from elsewhere is
   * compared with the DOM, in its rendered form (`<div><br></div>` for empty).
   */
  const editor = useRef<HTMLDivElement>(null)
  const reported = useRef<string | null>(null)
  useLayoutEffect(() => {
    const node = editor.current
    const own = reported.current === html
    reported.current = null
    if (node === null || own) return
    const view = defaultWrap === undefined ? html : taEditorFromModel(html, defaultWrap)
    if (node.innerHTML !== view) node.innerHTML = view
  }, [html, defaultWrap])

  return (
    <div className={`${className} ta-root`.trim()} onClick={onClick}>
      {placeholder === undefined ? null : (
        // textAngular writes this rule into the document's stylesheet at link time. Written
        // here it is the same rule, and it is scoped to the id the same way.
        <style data-ta-placeholder={styleKey}>{`#${id}.placeholder-text:before{content:"${placeholder}"}`}</style>
      )}
      <div className={`ta-scroll-window ta-text ta-editor form-control ${editorClassName}`.trim()}>
        <div
          ref={editor}
          id={id}
          className={`ta-bind ta-text${showsPlaceholder ? ' placeholder-text' : ''}${disabled ? ' ta-readonly' : ''}`}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={(event) => {
            const node = event.currentTarget
            // ⚠️ A DISABLED EDITOR NEVER REPORTS, AND PUTS THE MODEL BACK. Upstream's signature
            // editor dropped a textAngular commit that landed after CANCEL and wrote the saved
            // value back over it (woo@c7a5e20f87); dropping alone would leave this element
            // showing text the model does not hold, and the next keystroke after reopening would
            // report it. Nothing the user types reaches here — a disabled editor is not
            // contenteditable — so this only ever undoes a write from elsewhere.
            if (disabled) {
              const view = defaultWrap === undefined ? html : taEditorFromModel(html, defaultWrap)
              if (node.innerHTML !== view) node.innerHTML = view
              return
            }
            let value = node.innerHTML
            if (defaultWrap !== undefined) {
              // taBind's keyup (taBind.js:827-834): an editor emptied — select-all and delete —
              // gets `<div><br></div>` back with the caret inside it, so the next character is
              // wrapped like the first one was. Its content is gone, so there is no caret to lose.
              if (value === '<br>' || value.trim() === '') {
                node.innerHTML = taDefaultValue(defaultWrap)
                const selection = window.getSelection()
                if (selection !== null && node.firstChild !== null) {
                  selection.removeAllRanges()
                  const range = document.createRange()
                  range.setStart(node.firstChild, 0)
                  range.collapse(true)
                  selection.addRange(range)
                }
              }
              value = taModelFromEditor(node.innerHTML, defaultWrap)
            }
            reported.current = value
            onChange?.(value)
          }}
          onBlur={onBlur}
        />
      </div>
      {/* The hidden pair: the raw-HTML textarea the toolbar's html button swaps to, and the
          form input that always carries the correct value. Both are `display: none` here. */}
      <textarea className={`ta-html ta-editor form-control ${editorClassName}`.trim()} style={{ display: 'none' }} readOnly value={html} />
      <input type="hidden" tabIndex={-1} style={{ display: 'none' }} value={html} readOnly />
    </div>
  )
}
